// Phase 5 (web revival) — Library filter control.
//
// Segmented pill control + a month picker + an optional right slot
// (Slideshow / Select buttons), rendered as /library's top row. All
// filters are server-side except Favorites: the CONTRACT has no
// `favorite=true` param yet, so that tab signals "filter client-side on
// the loaded pages" and Library.tsx narrows the already-fetched assets.
// Photos/Videos push `kind=photo|video`; the month picker pushes
// `capture_after`/`capture_before` down to `/v1/assets`.
//
// Date filtering is one native <input type="month"> (replacing the old
// Year/Month tab pair + cascading dropdowns). The filter is always a
// whole calendar month. macOS Safari doesn't implement month inputs
// (it degrades to a bare text field), so it gets a two-select fallback
// with identical semantics.

import { useMemo, type ReactNode } from 'react';

export type LibraryFilterMode =
  | 'all'
  | 'favorites'
  | 'photos'
  | 'video'
  | 'date'
  | 'trash';

export interface LibraryFilter {
  mode: LibraryFilterMode;
  /// Calendar year when mode === 'date' (e.g. 2024).
  year?: number;
  /// Zero-based month index (0 = January) when mode === 'date'.
  month?: number;
}

interface FilterBarProps {
  value: LibraryFilter;
  onChange: (next: LibraryFilter) => void;
  /// Right-aligned content (e.g. a "Select" button). Optional.
  rightSlot?: ReactNode;
}

const TABS: { id: Exclude<LibraryFilterMode, 'date'>; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'favorites', label: 'Favorites' },
  { id: 'photos', label: 'Photos' },
  { id: 'video', label: 'Videos' },
  { id: 'trash', label: 'Trash' },
];

const MONTH_LABELS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const YEARS_BACK = 14;

function recentYears(): number[] {
  const now = new Date().getUTCFullYear();
  const out: number[] = [];
  for (let y = now; y >= now - YEARS_BACK; y--) out.push(y);
  return out;
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

// Feature-detect native month inputs once. Browsers without support
// (macOS Safari) silently downgrade the input to type="text".
const MONTH_INPUT_SUPPORTED =
  typeof document !== 'undefined' &&
  (() => {
    const probe = document.createElement('input');
    probe.setAttribute('type', 'month');
    return probe.type === 'month';
  })();

export function FilterBar({ value, onChange, rightSlot }: FilterBarProps) {
  const years = useMemo(recentYears, []);
  const bounds = useMemo(() => {
    const now = new Date();
    const y = now.getUTCFullYear();
    return {
      min: `${y - YEARS_BACK}-01`,
      max: `${y}-${pad2(now.getUTCMonth() + 1)}`,
    };
  }, []);

  const dateActive =
    value.mode === 'date' && value.year !== undefined && value.month !== undefined;
  const monthValue = dateActive ? `${value.year}-${pad2(value.month! + 1)}` : '';

  function applyMonthString(v: string) {
    // Empty fires while the user edits segments — ignore it; the explicit
    // clear path is the × button, so a half-typed value never silently
    // drops the active filter.
    if (!v) return;
    const [y, m] = v.split('-').map(Number);
    if (!Number.isFinite(y) || !Number.isFinite(m)) return;
    onChange({ mode: 'date', year: y, month: m - 1 });
  }

  return (
    <div className="flex min-h-12 flex-wrap items-center gap-x-2 gap-y-1.5 border-b border-white/5 px-4 py-1.5">
      <div className="flex items-center gap-0.5 rounded-full border border-white/10 bg-white/[0.03] p-0.5">
        {TABS.map((t) => {
          const active = value.mode === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => onChange({ mode: t.id })}
              aria-pressed={active}
              className={
                'rounded-full px-3 py-1 text-xs font-medium transition-colors ' +
                (active
                  ? 'bg-white/10 text-white'
                  : 'text-neutral-400 hover:text-neutral-200')
              }
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {MONTH_INPUT_SUPPORTED ? (
        <input
          type="month"
          value={monthValue}
          min={bounds.min}
          max={bounds.max}
          onChange={(e) => applyMonthString(e.target.value)}
          aria-label="Filter by month"
          title="Filter by month"
          className={
            'rounded-full border px-3 py-1 text-xs transition-colors [color-scheme:dark] ' +
            (dateActive
              ? 'border-white/20 bg-white/10 text-white'
              : 'border-white/10 bg-white/[0.03] text-neutral-400 hover:text-neutral-200')
          }
        />
      ) : (
        <FallbackMonthSelects
          years={years}
          active={dateActive}
          year={value.year}
          month={value.month}
          onChange={onChange}
        />
      )}

      {dateActive && (
        <button
          type="button"
          onClick={() => onChange({ mode: 'all' })}
          aria-label="Clear date filter"
          title="Clear date filter"
          className="flex size-6 items-center justify-center rounded-full text-neutral-400 transition-colors hover:bg-white/10 hover:text-white"
        >
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            aria-hidden
          >
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        </button>
      )}

      {rightSlot && <div className="ml-auto">{rightSlot}</div>}
    </div>
  );
}

// macOS Safari fallback: native month inputs degrade to text fields
// there, so render Month + Year selects with the same whole-month
// semantics. Picking either with the other unset defaults the year to
// the current year and the month to January.
function FallbackMonthSelects({
  years,
  active,
  year,
  month,
  onChange,
}: {
  years: number[];
  active: boolean;
  year?: number;
  month?: number;
  onChange: (next: LibraryFilter) => void;
}) {
  const selectClass =
    'rounded-full border border-white/10 bg-neutral-900 px-2.5 py-1 text-xs ' +
    (active ? 'text-neutral-100' : 'text-neutral-400');
  return (
    <div className="flex items-center gap-1.5">
      <select
        value={active ? month : ''}
        onChange={(e) => {
          if (e.target.value === '') return;
          onChange({
            mode: 'date',
            year: year ?? years[0]!,
            month: Number(e.target.value),
          });
        }}
        aria-label="Filter month"
        className={selectClass}
      >
        <option value="" disabled>
          Month
        </option>
        {MONTH_LABELS.map((label, idx) => (
          <option key={label} value={idx}>
            {label}
          </option>
        ))}
      </select>
      <select
        value={active ? year : ''}
        onChange={(e) => {
          if (e.target.value === '') return;
          onChange({
            mode: 'date',
            year: Number(e.target.value),
            month: month ?? 0,
          });
        }}
        aria-label="Filter year"
        className={selectClass}
      >
        <option value="" disabled>
          Year
        </option>
        {years.map((y) => (
          <option key={y} value={y}>
            {y}
          </option>
        ))}
      </select>
    </div>
  );
}
